import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listPostsAdmin, hidePost, CATEGORIES, categoryLabel } from '../api/community';
import SortableHeader from '../components/SortableHeader';

const STATUS_FILTERS = [
  { id: 'all',    label: '전체' },
  { id: 'active', label: '공개' },
  { id: 'hidden', label: '숨김' },
];

function getSortValue(p, key) {
  if (key === 'commentCount') return p.commentCount || 0;
  if (key === 'createdAt') return p.createdAt || null;
  if (key === 'isHidden') return p.isHidden ? 1 : 0;
  return p[key] || null;
}

function compare(a, b, key) {
  const av = getSortValue(a, key);
  const bv = getSortValue(b, key);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv), 'ko-KR');
}

function formatDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 16).replace('T', ' ');
}

export default function AdminCommunityPage() {
  const { getIdToken } = useAuth();
  const [category, setCategory] = useState('review');
  const [statusFilter, setStatusFilter] = useState('all');
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({});
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Initial / category-change fetch — first page only.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError(''); setCursor(null);
      try {
        const token = await getIdToken();
        if (!token) { if (active) { setError('로그인이 필요합니다'); setLoading(false); } return; }
        const data = await listPostsAdmin({ token, category, cursor: null });
        if (!active) return;
        setPosts(data.posts || []);
        setCursor(data.nextCursor || null);
      } catch (err) {
        if (active) setError(`조회 실패: ${err.message}`);
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [getIdToken, category]);

  // "더 보기" pagination — appends next page using current cursor.
  const fetchMore = useCallback(async () => {
    if (!cursor) return;
    setLoading(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) { setError('로그인이 필요합니다'); setLoading(false); return; }
      const data = await listPostsAdmin({ token, category, cursor });
      setPosts(prev => [...prev, ...(data.posts || [])]);
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError(`조회 실패: ${err.message}`);
    }
    setLoading(false);
  }, [getIdToken, category, cursor]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return posts;
    return posts.filter(p => statusFilter === 'hidden' ? p.isHidden : !p.isHidden);
  }, [posts, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => sortDir === 'desc' ? -compare(a, b, sortKey) : compare(a, b, sortKey));
    return arr;
  }, [filtered, sortKey, sortDir]);

  const handleToggleHide = async (post) => {
    const next = !post.isHidden;
    if (next && !window.confirm(`"${post.title}"을(를) 숨기시겠습니까?`)) return;
    setBusy(b => ({ ...b, [post.postId]: true }));
    try {
      const token = await getIdToken();
      const result = await hidePost({ token, postId: post.postId, isHidden: next });
      setPosts(prev => prev.map(p => p.postId === post.postId ? result.post : p));
    } catch (err) {
      alert(`실패: ${err.message}`);
    }
    setBusy(b => ({ ...b, [post.postId]: false }));
  };

  return (
    <div style={{ padding: '20px 28px' }}>
      <h2 style={{ marginBottom: 16 }}>📋 커뮤니티 모더레이션</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            style={tabStyle(category === c.id)}
          >{c.emoji} {c.label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            style={pillStyle(statusFilter === f.id)}
          >{f.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {sorted.length}건 · 💡 컬럼 클릭으로 정렬
        </span>
      </div>

      {error && <p style={{ color: '#ff5252' }}>❌ {error}</p>}

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <SortableHeader label="제목" sortKey="title" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="작성자" sortKey="nickname" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="카테고리" sortKey="category" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="작성일" sortKey="createdAt" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="💬" sortKey="commentCount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="상태" sortKey="isHidden" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <th style={{ ...thStyle, textAlign: 'right' }}>액션</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.postId} style={{ opacity: p.isHidden ? 0.55 : 1 }}>
                <td style={tdStyle}>
                  <Link
                    to={`/community/post/${encodeURIComponent(p.postId)}`}
                    style={{
                      color: 'var(--text)',
                      textDecoration: p.isHidden ? 'line-through' : 'none',
                    }}
                  >{p.title}</Link>
                </td>
                <td style={tdStyle}>{p.nickname || '익명'}</td>
                <td style={tdStyle}>{categoryLabel(p.category)}</td>
                <td style={tdStyle}>{formatDate(p.createdAt)}</td>
                <td style={tdStyle}>{p.commentCount || 0}</td>
                <td style={tdStyle}>
                  <span style={p.isHidden ? badgeHidden : badgeActive}>
                    {p.isHidden ? '숨김' : '공개'}
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button
                    onClick={() => handleToggleHide(p)}
                    disabled={busy[p.postId]}
                    style={actionBtn(p.isHidden)}
                  >
                    {busy[p.postId] ? '⏳' : (p.isHidden ? '복원' : '숨김')}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>글이 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {cursor && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button onClick={fetchMore} className="btn btn-outline" disabled={loading}>
            {loading ? '...' : '더 보기'}
          </button>
        </div>
      )}
    </div>
  );
}

const tabStyle = (active) => ({
  padding: '8px 14px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  background: active ? 'var(--primary)' : 'var(--surface)',
  color: active ? '#fff' : 'var(--text)',
  cursor: 'pointer',
  fontSize: '0.9rem',
});

const pillStyle = (active) => ({
  padding: '6px 12px',
  border: '1px solid var(--border)',
  borderRadius: 16,
  background: active ? 'var(--text)' : 'var(--bg)',
  color: active ? 'var(--bg)' : 'var(--text-muted)',
  cursor: 'pointer',
  fontSize: '0.82rem',
});

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};

const thStyle = {
  padding: '10px 12px',
  borderBottom: '2px solid var(--border)',
  textAlign: 'left',
  background: 'var(--surface)',
  fontWeight: 600,
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

const badgeActive = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
  fontSize: '0.75rem', background: 'rgba(74,159,255,0.15)', color: 'var(--primary)',
};
const badgeHidden = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
  fontSize: '0.75rem', background: 'rgba(255,82,82,0.15)', color: '#ff5252',
};

const actionBtn = (isHidden) => ({
  padding: '4px 12px',
  border: `1px solid ${isHidden ? '#4a9fff' : '#ff5252'}`,
  borderRadius: 6,
  background: 'transparent',
  color: isHidden ? '#4a9fff' : '#ff5252',
  cursor: 'pointer',
  fontSize: '0.82rem',
});
